import React, { useEffect, useState } from "react";
import { patientService } from "../services/patientService";
import { branchService } from "../services/branchService";
import { testMasterService } from "../services/testMasterService";
import { vitalTestService } from "../services/vitalTestService";
import Card from "../components/common/Card";
import { format } from "date-fns";
import Layout from "../components/layout/Layout";
import { useAuth } from "../hooks/useAuth";
import { FaHospital, FaVial, FaFlask, FaUserMd, FaCalendarCheck } from "react-icons/fa";

const statusColors = {
  scheduled: "bg-yellow-100 text-yellow-700 border-yellow-200",
  completed: "bg-green-100 text-green-700 border-green-200",
  pending: "bg-orange-100 text-orange-700 border-orange-200",
  cancelled: "bg-gray-100 text-gray-700 border-gray-200",
};

// Function to determine color class based on completion percentage
const getCompletionColorClass = (completionRatio) => {
  const percentage = completionRatio * 100;
  
  if (percentage >= 80) {
    return 'bg-green-500'; // High completion - green
  } else if (percentage >= 50) {
    return 'bg-yellow-500'; // Medium completion - yellow
  } else if (percentage >= 25) {
    return 'bg-orange-500'; // Low completion - orange
  } else {
    return 'bg-red-500'; // Very low completion - red
  }
};

const DashboardPage = () => {
  const { currentUser } = useAuth();
  const [stats, setStats] = useState({
    total: 0,
    byStatus: {},
    today: [],
    dexaCount: 0,
    vo2maxCount: 0,
    bloodCount: 0,
    last7Days: {
      total: 0,
      dexaCount: 0,
      vo2maxCount: 0,
      bloodCount: 0,
      withReportUrl: {
        dexaCount: 0,
        vo2maxCount: 0,
        bloodCount: 0
      }
    }
  });
  const [branchStats, setBranchStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    cityCounts: {},
    topCities: [],
  });
  const [biomarkerStats, setBiomarkerStats] = useState({
    total: 0,
    uniqueTestCodes: 0,
    maleSpecific: 0,
    femaleSpecific: 0,
    bothGenders: 0,
  });
  const [testStats, setTestStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    testCount: 0,
    packageCount: 0,
  });
  const [loading, setLoading] = useState({
    patients: true,
    branches: true,
    biomarkers: true,
    tests: true,
  });

  // Function to fetch patient stats - can be called for refresh
  const fetchPatientStats = async () => {
      setLoading(prev => ({ ...prev, patients: true }));
      try {
        const from_date = format(
          new Date(Date.now() - 1000 * 60 * 60 * 24 * 30),
          "yyyy-MM-dd"
        );
        const to_date = format(new Date(), "yyyy-MM-dd");
        const resp = await patientService.getPatients({ startDate: from_date, endDate: to_date });
        
        if (resp.data?.success && Array.isArray(resp.data.data)) {
          const all = resp.data.data;
          const byStatus = all.reduce((acc, a) => {
            acc[a.appointment_status] = (acc[a.appointment_status] || 0) + 1;
            return acc;
          }, {});
          const todayStr = format(new Date(), "yyyy-MM-dd");
          
          // Get today's appointments with test type information
          const today = all.filter((a) => (a.slot_start_time || "").slice(0, 10) === todayStr)
            .map(appointment => {
              // Add test type information
              const testTypes = [];
              
              if (appointment.appointmentTests && Array.isArray(appointment.appointmentTests)) {
                // Check for DEXA test
                if (appointment.appointmentTests.some(test => test.availableTest?.test_code === '003554')) {
                  testTypes.push({ type: 'DEXA', description: 'Body Composition Analysis' });
                }
                
                // Check for VO2MAX test
                if (appointment.appointmentTests.some(test => test.availableTest?.test_code === '003553')) {
                  testTypes.push({ type: 'VO2MAX', description: 'Cardiorespiratory Fitness Test' });
                }
                
                // Check for blood test
                if (appointment.appointmentTests.some(test => 
                  test.availableTest?.test_code && test.availableTest.test_code.startsWith('P')
                )) {
                  testTypes.push({ type: 'Blood', description: 'Comprehensive Blood Analysis' });
                }
              }
              
              return {
                ...appointment,
                testTypes
              };
            });
          
          // Count patients with different test types
          let dexaCount = 0;
          let vo2maxCount = 0;
          let bloodCount = 0;
          
          // Last 7 days statistics
          const sevenDaysAgo = new Date();
          sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 14);
          const sevenDaysAgoStr = format(sevenDaysAgo, "yyyy-MM-dd");
          
          const last7DaysAppointments = all.filter(a => {
            const appointmentDate = (a.slot_start_time || "").slice(0, 10);
            return appointmentDate >= sevenDaysAgoStr && appointmentDate <= todayStr;
          });
          
          let last7Days = {
            total: last7DaysAppointments.length,
            dexaCount: 0,
            vo2maxCount: 0,
            bloodCount: 0,
            withReportUrl: {
              dexaCount: 0,
              vo2maxCount: 0,
              bloodCount: 0
            }
          };
          
          all.forEach(patient => {
            if (patient.appointmentTests && Array.isArray(patient.appointmentTests)) {
              // Check for DEXA test (003554)
              if (patient.appointmentTests.some(test => test.availableTest?.test_code === '003554')) {
                dexaCount++;
              }
              
              // Check for VO2MAX test (003553)
              if (patient.appointmentTests.some(test => test.availableTest?.test_code === '003553')) {
                vo2maxCount++;
              }
              
              // Check for blood test (starts with P)
              if (patient.appointmentTests.some(test => 
                test.availableTest?.test_code && test.availableTest.test_code.startsWith('P')
              )) {
                bloodCount++;
              }
            }
          });
          
          // Process last 7 days appointments
          last7DaysAppointments.forEach(patient => {
            // Check for test types
            if (patient.appointmentTests && Array.isArray(patient.appointmentTests)) {
              // Check for DEXA test
              if (patient.appointmentTests.some(test => test.availableTest?.test_code === '003554')) {
                last7Days.dexaCount++;
                
                // Check if DEXA report URL exists
                const dexaReport = patient.report_urls?.find(report => report.report_type === 'dexaReport');
                if (dexaReport?.report_url || patient.dexaReport?.pdf_url) {
                  last7Days.withReportUrl.dexaCount++;
                }
              }
              
              // Check for VO2MAX test
              if (patient.appointmentTests.some(test => test.availableTest?.test_code === '003553')) {
                last7Days.vo2maxCount++;
                
                // Check if VO2MAX report URL exists
                const vo2maxReport = patient.report_urls?.find(report => report.report_type === 'vo2maxReport');
                if (vo2maxReport?.report_url || patient.vo2maxReport?.pdf_url) {
                  last7Days.withReportUrl.vo2maxCount++;
                }
              }
              
              // Check for blood test
              if (patient.appointmentTests.some(test => 
                test.availableTest?.test_code && test.availableTest.test_code.startsWith('P')
              )) {
                last7Days.bloodCount++;
                
                // Check if blood report URL exists
                const bloodReport = patient.report_urls?.find(report => report.report_type === 'bloodReport');
                if (bloodReport?.report_url) {
                  last7Days.withReportUrl.bloodCount++;
                }
              }
            }
          });
          
          setStats({ 
            total: all.length, 
            byStatus, 
            today,
            dexaCount,
            vo2maxCount,
            bloodCount,
            last7Days
          });
        }
      } catch (error) {
        console.error('Error fetching patient stats:', error);
        setStats({ 
          total: 0, 
          byStatus: {}, 
          today: [],
          dexaCount: 0,
          vo2maxCount: 0,
          bloodCount: 0,
          last7Days: {
            total: 0,
            dexaCount: 0,
            vo2maxCount: 0,
            bloodCount: 0,
            withReportUrl: {
              dexaCount: 0,
              vo2maxCount: 0,
              bloodCount: 0
            }
          }
        });
      }
      setLoading(prev => ({ ...prev, patients: false }));
    };

    const fetchBranchStats = async () => {
      setLoading(prev => ({ ...prev, branches: true }));
      try {
        const resp = await branchService.getBranches();
        if (resp.data?.success && Array.isArray(resp.data.data)) {
          const branches = resp.data.data;
          const active = branches.filter(branch => branch.is_active).length;
          
          // Group branches by city
          const cityCounts = {};
          branches.forEach(branch => {
            // Get city directly from city_name property
            let city = branch.city_name || 'Unknown';
            
            // Normalize city name if it exists
            if (city !== 'Unknown') {
              city = city.charAt(0).toUpperCase() + city.slice(1).toLowerCase();
            }
            
            // Count branches in this city
            cityCounts[city] = (cityCounts[city] || 0) + 1;
          });
          
          // Get top cities by branch count
          const topCities = Object.entries(cityCounts)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 2)
            .map(([city, count]) => ({ city, count }));
          
          setBranchStats({
            total: branches.length,
            active,
            inactive: branches.length - active,
            cityCounts,
            topCities
          });
        }
      } catch (error) {
        console.error('Error fetching branch stats:', error);
        setBranchStats({ 
          total: 0, 
          active: 0, 
          inactive: 0,
          cityCounts: {},
          topCities: []
        });
      }
      setLoading(prev => ({ ...prev, branches: false }));
    };

    const fetchBiomarkerStats = async () => {
      setLoading(prev => ({ ...prev, biomarkers: true }));
      try {
        const resp = await testMasterService.getTestMasters();
        if (resp.data?.success && Array.isArray(resp.data.data)) {
          const biomarkers = resp.data.data;
          const maleSpecific = biomarkers.filter(b => b.gender === 'male').length;
          const femaleSpecific = biomarkers.filter(b => b.gender === 'female').length;
          const bothGenders = biomarkers.filter(b => !b.gender || b.gender === 'both').length;
          
          // Count unique test codes
          const uniqueTestCodes = new Set();
          biomarkers.forEach(biomarker => {
            if (biomarker.test_code) {
              uniqueTestCodes.add(biomarker.test_code);
            }
          });
          
          setBiomarkerStats({
            total: biomarkers.length,
            uniqueTestCodes: uniqueTestCodes.size,
            maleSpecific,
            femaleSpecific,
            bothGenders
          });
        }
      } catch (error) {
        console.error('Error fetching biomarker stats:', error);
        setBiomarkerStats({ 
          total: 0, 
          uniqueTestCodes: 0,
          maleSpecific: 0, 
          femaleSpecific: 0, 
          bothGenders: 0 
        });
      }
      setLoading(prev => ({ ...prev, biomarkers: false }));
    };

    const fetchTestStats = async () => {
      setLoading(prev => ({ ...prev, tests: true }));
      try {
        const resp = await vitalTestService.getVitalTests();
        if (resp.data?.success && Array.isArray(resp.data.data)) {
          const tests = resp.data.data;
          const active = tests.filter(test => test.is_active).length;
          
          // Count tests and packages by test_type
          const testCount = tests.filter(test => test.test_type === 'TEST').length;
          const packageCount = tests.filter(test => test.test_type === 'PACKAGE').length;
          
          setTestStats({
            total: tests.length,
            active,
            inactive: tests.length - active,
            testCount,
            packageCount
          });
        }
      } catch (error) {
        console.error('Error fetching test stats:', error);
        setTestStats({ 
          total: 0, 
          active: 0, 
          inactive: 0,
          testCount: 0,
          packageCount: 0
        });
      }
      setLoading(prev => ({ ...prev, tests: false }));
    };

  // Call all data fetching functions on component mount
  useEffect(() => {
    fetchPatientStats();
    fetchBranchStats();
    fetchBiomarkerStats();
    fetchTestStats();
  }, []);

  return (
    <Layout>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-bold text-gray-800">Welcome back, {currentUser?.name || 'User'}!</h3>
          <p className="text-gray-500">{format(new Date(), "EEEE, MMMM d, yyyy")}</p>
        </div>

        {/* Main Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-none">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-blue-500 text-white mr-4">
                <FaUserMd size={24} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-blue-700">Orders</h3>
                <p className="text-3xl font-bold text-blue-800">
                  {loading.patients ? "..." : stats.total}
                </p>
                <div className="flex text-sm mt-1 flex-wrap">
                  <span className="text-purple-500 mr-2">{stats.dexaCount} DEXA</span>
                  <span className="text-red-500 mr-2">{stats.vo2maxCount} VO2</span>
                  <span className="text-green-500">{stats.bloodCount} Blood</span>
                </div>
              </div>
            </div>
          </Card>

          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-none">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-green-500 text-white mr-4">
                <FaHospital size={24} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-green-700">Branches</h3>
                <p className="text-3xl font-bold text-green-800">
                  {loading.branches ? "..." : branchStats.total}
                </p>
                <div className="flex text-sm mt-1 flex-wrap">
                  {loading.branches ? (
                    <span className="text-gray-500">Loading...</span>
                  ) : branchStats.topCities.length > 0 ? (
                    branchStats.topCities.map((city, index) => (
                      <span key={city.city} className={`${index === 0 ? 'text-green-600' : 'text-teal-500'} mr-2`}>
                        {city.city}: {city.count}
                      </span>
                    ))
                  ) : (
                    <span className="text-green-600 mr-2">{branchStats.active} active</span>
                  )}
                </div>
              </div>
            </div>
          </Card>

          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-none">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-purple-500 text-white mr-4">
                <FaFlask size={24} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-purple-700">Biomarkers</h3>
                <p className="text-3xl font-bold text-purple-800">
                  {loading.biomarkers ? "..." : biomarkerStats.total}
                </p>
                <div className="flex text-sm mt-1 flex-wrap">
                  <span className="text-indigo-600 mr-2">{biomarkerStats.uniqueTestCodes} unique codes</span>
                </div>
              </div>
            </div>
          </Card>

          <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-none">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-amber-500 text-white mr-4">
                <FaVial size={24} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-amber-700">Tests</h3>
                <p className="text-3xl font-bold text-amber-800">
                  {loading.tests ? "..." : testStats.total}
                </p>
                <div className="flex text-sm mt-1">
                  <span className="text-amber-600 mr-2">{testStats.testCount} tests</span>
                  <span className="text-orange-500">{testStats.packageCount} packages</span>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Appointment Status Cards */}
        {/* <h2 className="text-xl font-bold text-gray-800 mb-4">Appointment Status</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
          {["scheduled", "completed", "pending", "cancelled"].map((status) => (
            <Card 
              key={status} 
              className={`${statusColors[status]} border`}
            >
              <div className="flex items-center">
                <div className="p-3 rounded-full bg-white mr-4">
                  <FaCalendarCheck size={20} className={status === "scheduled" ? "text-yellow-500" : 
                                              status === "completed" ? "text-green-500" : 
                                              status === "pending" ? "text-orange-500" : "text-gray-500"} />
                </div>
                <div>
                  <h3 className="font-medium capitalize">{status}</h3>
                  <p className="text-2xl font-bold mt-1">
                    {loading.patients ? "..." : stats.byStatus[status] || 0}
                  </p>
                </div>
              </div>
            </Card>
          ))}
        </div> */}

        {/* Last 7 Days Test Statistics */}
        <h2 className="text-xl font-bold text-gray-800 mb-4">Last 2 weeks Test Statistics</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* DEXA Tests Card */}
          <Card className="border border-purple-200 p-5">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-semibold text-purple-700">DEXA Tests</h3>
              <span className="text-2xl font-bold text-purple-800">
                {loading.patients ? "..." : stats.last7Days.dexaCount}
              </span>
            </div>
            <div className="text-xs text-gray-500 mb-4">Body Composition Analysis</div>
            
            {/* Progress Bar */}
            <div className="mb-2">
              <div className="w-full bg-gray-200 rounded-full h-2 mb-1">
                {!loading.patients && stats.last7Days.dexaCount > 0 && (
                  <div 
                    className={`h-2 rounded-full ${getCompletionColorClass(stats.last7Days.withReportUrl.dexaCount / stats.last7Days.dexaCount)}`}
                    style={{ width: `${Math.min(100, Math.round((stats.last7Days.withReportUrl.dexaCount / stats.last7Days.dexaCount) * 100))}%` }}
                  ></div>
                )}
              </div>
              <div className="flex justify-between text-xs">
                <span>Reports Generated</span>
                <span className="font-medium">
                  {loading.patients ? "..." : `${stats.last7Days.withReportUrl.dexaCount} / ${stats.last7Days.dexaCount}`}
                  <span className="ml-1">
                    {stats.last7Days.dexaCount > 0 ? 
                      `(${Math.round((stats.last7Days.withReportUrl.dexaCount / stats.last7Days.dexaCount) * 100)}%)` : 
                      '(0%)'
                    }
                  </span>
                </span>
              </div>
            </div>
          </Card>

          {/* VO2MAX Tests Card */}
          <Card className="border border-red-200 p-5">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-semibold text-red-700">VO2MAX Tests</h3>
              <span className="text-2xl font-bold text-red-800">
                {loading.patients ? "..." : stats.last7Days.vo2maxCount}
              </span>
            </div>
            <div className="text-xs text-gray-500 mb-4">Cardiorespiratory Fitness Test</div>
            
            {/* Progress Bar */}
            <div className="mb-2">
              <div className="w-full bg-gray-200 rounded-full h-2 mb-1">
                {!loading.patients && stats.last7Days.vo2maxCount > 0 && (
                  <div 
                    className={`h-2 rounded-full ${getCompletionColorClass(stats.last7Days.withReportUrl.vo2maxCount / stats.last7Days.vo2maxCount)}`}
                    style={{ width: `${Math.min(100, Math.round((stats.last7Days.withReportUrl.vo2maxCount / stats.last7Days.vo2maxCount) * 100))}%` }}
                  ></div>
                )}
              </div>
              <div className="flex justify-between text-xs">
                <span>Reports Generated</span>
                <span className="font-medium">
                  {loading.patients ? "..." : `${stats.last7Days.withReportUrl.vo2maxCount} / ${stats.last7Days.vo2maxCount}`}
                  <span className="ml-1">
                    {stats.last7Days.vo2maxCount > 0 ? 
                      `(${Math.round((stats.last7Days.withReportUrl.vo2maxCount / stats.last7Days.vo2maxCount) * 100)}%)` : 
                      '(0%)'
                    }
                  </span>
                </span>
              </div>
            </div>
          </Card>

          {/* Blood Tests Card */}
          <Card className="border border-green-200 p-5">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-semibold text-green-700">Blood Tests</h3>
              <span className="text-2xl font-bold text-green-800">
                {loading.patients ? "..." : stats.last7Days.bloodCount}
              </span>
            </div>
            <div className="text-xs text-gray-500 mb-4">Comprehensive Blood Analysis</div>
            
            {/* Progress Bar */}
            <div className="mb-2">
              <div className="w-full bg-gray-200 rounded-full h-2 mb-1">
                {!loading.patients && stats.last7Days.bloodCount > 0 && (
                  <div 
                    className={`h-2 rounded-full ${getCompletionColorClass(stats.last7Days.withReportUrl.bloodCount / stats.last7Days.bloodCount)}`}
                    style={{ width: `${Math.min(100, Math.round((stats.last7Days.withReportUrl.bloodCount / stats.last7Days.bloodCount) * 100))}%` }}
                  ></div>
                )}
              </div>
              <div className="flex justify-between text-xs">
                <span>Reports Generated</span>
                <span className="font-medium">
                  {loading.patients ? "..." : `${stats.last7Days.withReportUrl.bloodCount} / ${stats.last7Days.bloodCount}`}
                  <span className="ml-1">
                    {stats.last7Days.bloodCount > 0 ? 
                      `(${Math.round((stats.last7Days.withReportUrl.bloodCount / stats.last7Days.bloodCount) * 100)}%)` : 
                      '(0%)'
                    }
                  </span>
                </span>
              </div>
            </div>
          </Card>
        </div>

        <Card className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center">
              <h2 className="text-lg font-semibold text-gray-800">Today's Appointments</h2>
              <button 
                onClick={() => fetchPatientStats()} 
                className="ml-2 p-1 rounded-full hover:bg-gray-100 transition-colors"
                title="Refresh appointments"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
            </div>
            <span className="text-sm text-gray-500">
              {stats.today.length} appointment{stats.today.length !== 1 ? 's' : ''} today
            </span>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Patient</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Branch</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Test</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Report Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading.patients ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-4 text-center text-gray-500">Loading...</td>
                  </tr>
                ) : stats.today.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-4 text-center text-gray-500">No appointments scheduled for today</td>
                  </tr>
                ) : (
                  stats.today.map((appointment) => (
                    <tr key={appointment.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center">
                          <div className="ml-1">
                            <div className="text-sm font-medium text-gray-900">{appointment.profile?.name || "-"}</div>
                            <div className="text-xs text-gray-500">{appointment.profile?.gender || "-"}, {appointment.profile?.age || "-"} yrs</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-900">
                          {appointment.facility?.branch_name ? 
                            appointment.facility.branch_name.replace('VITAL INSIGHTS-', '').split('-')[0] : "-"}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {appointment.testTypes && appointment.testTypes.map((test, index) => (
                            <span 
                              key={index} 
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${test.type === 'DEXA' ? 'bg-purple-100 text-purple-800' : test.type === 'VO2MAX' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}
                              title={test.description}
                            >
                              {test.type}
                            </span>
                          ))}
                          {(!appointment.testTypes || appointment.testTypes.length === 0) && (
                            <span className="text-xs text-gray-500">-</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {appointment.testTypes && appointment.testTypes.map((test, index) => {
                            // Check if report URL exists for this test type
                            let hasReport = false;
                            if (test.type === 'DEXA') {
                              const dexaReport = appointment.report_urls?.find(report => report.report_type === 'dexaReport');
                              hasReport = !!(dexaReport?.report_url || appointment.dexaReport?.pdf_url);
                            } else if (test.type === 'VO2MAX') {
                              const vo2maxReport = appointment.report_urls?.find(report => report.report_type === 'vo2maxReport');
                              hasReport = !!(vo2maxReport?.report_url || appointment.vo2maxReport?.pdf_url);
                            } else if (test.type === 'Blood') {
                              const bloodReport = appointment.report_urls?.find(report => report.report_type === 'bloodReport');
                              hasReport = !!bloodReport?.report_url;
                            }
                            
                            return (
                              <span 
                                key={`status-${index}`}
                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${hasReport ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}
                              >
                                {hasReport ? 'Generated' : 'Pending'}
                              </span>
                            );
                          })}
                          {(!appointment.testTypes || appointment.testTypes.length === 0) && (
                            <span className="text-xs text-gray-500">-</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {appointment.slot_object?.[0]?.slotTime || "-"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </Layout>
  );
};

export default DashboardPage;